import { useState, useEffect } from 'react';
import './RetailModel.css';
import { Modal } from 'react-bootstrap';
import { useAuth } from '../../auth';
import { VehicleForm, VehicleFormValues } from './VehicleForm';
import { VehicleList, VehicleData } from './VehicleList';
import { KTSVG } from '../../../../_metronic/helpers/components/KTSVG';

type Props = {
  show: boolean;
  handleClose: () => void;
};

const WEBHOOK_URL = 'https://etl.futurisk.in/webhook/98b8d78b-0b1e-40e9-8859-36657622bfd2';
const LOAD_VEHICLES_URL = 'https://etl.futurisk.in/webhook/d9243eee-4805-496d-b43e-8c1d01ea1d35';

const RetailModel: React.FC<Props> = ({ show, handleClose }) => {
  const auth = useAuth();
  const [vehicles, setVehicles] = useState<VehicleData[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [redirectingUrl, setRedirectingUrl] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Load vehicles when modal opens
  useEffect(() => {
    let mounted = true;
    const loadVehicles = async () => {
      if (!show) {
        // Reset state when modal closes
        setVehicles([]);
        setShowForm(false);
        setIsSubmitted(false);
        setSubmissionError(null);
        setRedirectingUrl(null);
        return;
      }
      
      setLoading(true);
      setShowForm(false);
      setIsSubmitted(false);
      setSubmissionError(null);
      setRedirectingUrl(null);
      setVehicles([]);
      
      try {
        const payload = {
          CRMClientId: auth.currentUser?.CRMClientId,
          EmployeeId: auth.currentUser?.EmployeeId,
        };

        const resp = await fetch(LOAD_VEHICLES_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(auth.currentUser?.sessioncode ? { SessionCode: auth.currentUser.sessioncode } : {}),
          },
          body: JSON.stringify(payload),
        });

        if (!mounted) return;

        if (resp.ok) {
          const data = await resp.json();
          // Handle both single object and array responses
          let vehicleList: VehicleData[];
          if (Array.isArray(data)) {
            vehicleList = data;
          } else if (data && typeof data === 'object' && data.ID) {
            // Single vehicle returned as object
            vehicleList = [data];
          } else {
            vehicleList = [];
          }
          setVehicles(vehicleList);
          
          // If no vehicles, show form directly
          if (vehicleList.length === 0) {
            setShowForm(true);
          } else {
            setShowForm(false);
          }
        } else {
          // On error, show form
          setVehicles([]);
          setShowForm(true);
        }
      } catch (err) {
        if (mounted) {
          // On error, show form
          setVehicles([]);
          setShowForm(true);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadVehicles();
    return () => { mounted = false; };
  }, [show, auth.currentUser]);

  const handleFormSubmit = async (values: VehicleFormValues) => {
    setSubmissionError(null);
    setCreating(true);
    try {
      const payload = {
        Name: values.Name,
        emailId: values.emailId,
        registrationNumber: values.registrationNumber,
        phoneNumber: values.phoneNumber,
        CRMClientId: auth.currentUser?.CRMClientId,
        EmployeeId: auth.currentUser?.EmployeeId,
      };

      const resp = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(auth.currentUser?.sessioncode ? { SessionCode: auth.currentUser.sessioncode } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`Server error: ${resp.status} ${text}`);
      }

      const data = await resp.json();

      let redirectUrl: string | undefined;
      try {
        if (Array.isArray(data) && data.length > 0 && data[0].body?.responseData?.url) {
          redirectUrl = data[0].body.responseData.url;
        } else if (data?.body?.responseData?.url) {
          redirectUrl = data.body.responseData.url;
        } else if (data?.responseData?.url) {
          redirectUrl = data.responseData.url;
        }
      } catch (e) {
        // ignore parsing errors
      }

      setIsSubmitted(true);

      if (redirectUrl) {
        setRedirectingUrl(redirectUrl);
        try { window.open(redirectUrl, '_blank'); } catch (e) {}
        setTimeout(() => handleClose(), 800);
      } else {
        // No redirect URL - reload vehicles to show the new entry
        setTimeout(async () => {
          setIsSubmitted(false);
          setShowForm(false);
          setLoading(true);
          // Reload vehicles
          try {
            const loadPayload = {
              CRMClientId: auth.currentUser?.CRMClientId,
              EmployeeId: auth.currentUser?.EmployeeId,
            };
            const loadResp = await fetch(LOAD_VEHICLES_URL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(auth.currentUser?.sessioncode ? { SessionCode: auth.currentUser.sessioncode } : {}),
              },
              body: JSON.stringify(loadPayload),
            });
            if (loadResp.ok) {
              const loadData = await loadResp.json();
              let reloadedVehicles: VehicleData[];
              if (Array.isArray(loadData)) {
                reloadedVehicles = loadData;
              } else if (loadData && typeof loadData === 'object' && loadData.ID) {
                reloadedVehicles = [loadData];
              } else {
                reloadedVehicles = [];
              }
              setVehicles(reloadedVehicles);
            }
          } catch (e) {
            // ignore reload errors
          } finally {
            setLoading(false);
          }
        }, 1500);
      }
    } catch (err: any) {
      setSubmissionError(err?.message || 'Submission failed');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      className='modal fade'
      id='kt_modal_retail'
      data-backdrop='static'
      tabIndex={-1}
      role='dialog'
      show={show}
      aria-hidden='true'
      dialogClassName=' modal-lg'
      onHide={handleClose}
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h5 className='modal-title'>Vehicle Vault</h5>
          <div className='btn btn-icon btn-sm btn-active-light-primary ms-2' onClick={handleClose}>
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-3x' />
          </div>

        </div>
        <div className='modal-body'>
          {loading ? (
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-2">Loading vehicles...</div>
            </div>
          ) : redirectingUrl ? (
            <div className="text-center">
              <div className="mb-3">Redirecting you...</div>
              <div>If nothing happens, <a href={redirectingUrl} target="_blank" rel="noreferrer">click here</a>.</div>
            </div>
          ) : isSubmitted ? (
            <div className="alert alert-success" role="alert">Details submitted successfully!</div>
          ) : (showForm || vehicles.length === 0) ? (
            <VehicleForm 
                submissionError={submissionError}
                creating={creating}
                onSubmit={handleFormSubmit}
                initialName={auth.currentUser?.Name || ''}
                initialEmail={auth.currentUser?.Email! || ''}
                initialPhone={auth.currentUser?.PhoneNumber! || ''}
              />
          ) : (
            <VehicleList 
                vehicles={vehicles}
                onAddVehicle={() => setShowForm(true)}
              />
          )}
        </div>
        <div className='modal-footer'>
          * terms and conditions apply
        </div>
      </div>
    </Modal>
  );
};

export { RetailModel };